import { PublicClientApplication } from '@azure/msal-browser';

import { AUTH_SCOPES, Constants, EXTENDED_AUTH_SCOPES } from './constants.ts';

export type GraphObject = {
  id?: string;
  name?: string;
  isFolder?: boolean;
  siteId?: string;
  driveId?: string;
  parentId?: string;
  children?: GraphObject[];
  size?: number;
};

export class GraphClient {
  accessToken?: string;
  filter?: string;

  constructor(accessToken?: string, filter?: string) {
    this.accessToken = accessToken;
    this.filter = filter;
  }

  async search(queryString: string): Promise<GraphObject[]> {
    const res = await this.execute(`https://graph.microsoft.com/v1.0/search/query`, 'POST', {
      requests: [
        {
          entityTypes: ['driveItem'],
          query: { queryString },
        },
      ],
    });

    return (
      res?.value?.[0]?.hitsContainers?.[0]?.hits?.map((it) =>
        this.toGraphObject(
          it.resource,
          it.resource.parentReference?.siteId,
          it.resource.parentReference?.driveId,
          it.resource.parentReference?.id,
          it.resource.size,
          it.file == null || it.folder != null
        )
      ) || []
    );
  }

  async get(driveId: string, id: string): Promise<GraphObject> {
    const res = await this.execute(`https://graph.microsoft.com/v1.0/drives/${driveId}/items/${id}`, 'GET');

    return this.toGraphObject({ id: res.id, name: res.name }, undefined, driveId, res.parentReference?.id);
  }

  async getWorksheets(siteId: string, driveId: string, itemId: string): Promise<GraphObject[]> {
    const res = await this.execute(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${itemId}/workbook/worksheets`,
      'GET'
    );

    return res?.value;
  }

  async getTables(siteId: string, driveId: string, itemId: string, worksheetId: string): Promise<GraphObject[]> {
    const res = await this.execute(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${itemId}/workbook/worksheets/${worksheetId}/tables`,
      'GET'
    );
    return res?.value;
  }

  async getTableColumns(siteId: string, driveId: string, itemId: string, tableId: string): Promise<GraphObject[]> {
    const res = await this.execute(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${itemId}/workbook/tables/${tableId}/columns`,
      'GET'
    );

    return res?.value;
  }

  async execute(endpoint: string, method: string, payload?: object) {
    if (!this.accessToken) return {};

    const headers = new Headers({
      Authorization: `Bearer ${this.accessToken}`,
      ...(payload ? { 'Content-Type': 'application/json' } : {}),
    });

    const result = await fetch(endpoint, { method, headers, body: JSON.stringify(payload) });
    return result.json();
  }

  toGraphObject(
    { id, name }: Partial<GraphObject> & { folder?: string },
    siteId?: string,
    driveId?: string,
    parentId?: string,
    size?: number,
    isFolder?: boolean
  ): GraphObject {
    return {
      id,
      name,
      siteId,
      driveId,
      parentId,
      isFolder,
      size,
    };
  }
}

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: Constants.ClientId?.toString(),
    authority: Constants.Authority?.toString(),
    redirectUri: Constants.RedirectUri?.toString(),
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: console.log,
    },
  },
});

export const basicLoginRequest = { scopes: AUTH_SCOPES };
export const extendedLoginRequest = { scopes: EXTENDED_AUTH_SCOPES };
